import { useState, type FormEvent } from 'react';
import { Button } from '../../components/Button';
import { dataRequestErrorMessage, submitDataRequest } from './repository';
import { m } from '../../paraglide/messages';

export interface DataRequestFormProps {
  readonly placeholder: string;
  /** The SKU the reader was looking at, when there is one. */
  readonly sku?: string;
  /** Signed-in surfaces post with the customer's credential, so the gateway
   *  already knows who sent it and there is no reply address to collect. */
  readonly signedIn?: boolean;
  /** Dismisses the surrounding modal, from Cancel and from the confirmation. */
  readonly onClose: () => void;
}

type SendPhase = 'idle' | 'sending' | 'sent';

const fieldClass =
  'w-full rounded-10 border border-line bg-surface px-3 py-2.5 text-14 leading-[1.5] text-text outline-none transition-colors placeholder:text-text-3 focus:border-accent-line';

/** The path a submission came from, read at send time (never during SSR). */
const currentPage = (): string => globalThis.location?.pathname ?? '';

/**
 * The message itself: textarea, an optional reply address when nobody is signed
 * in, and Send. There is no bot check in front of it - a sentence about missing
 * data is worth less to a spammer than the box is to the reader, and the route
 * is already bounded by the gateway's per-IP limit on its public group.
 *
 * It is separate from the modal that carries it so the send path stays a plain
 * React tree - the dialog around it is Radix, which needs a DOM these tests do
 * not have.
 */
export const DataRequestForm = ({
  placeholder,
  sku,
  signedIn = false,
  onClose,
}: DataRequestFormProps) => {
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState('');

  // Local state, not a query client: one message goes out and nothing about it
  // is cached, so the form works on any surface without a provider above it.
  const [phase, setPhase] = useState<SendPhase>('idle');
  const [failure, setFailure] = useState('');

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (message.trim() === '' || phase === 'sending') return;
    setPhase('sending');
    setFailure('');
    try {
      await submitDataRequest({
        message,
        sku,
        page: currentPage(),
        contact: signedIn ? undefined : contact,
      });
      setPhase('sent');
    } catch (error) {
      setFailure(dataRequestErrorMessage(error));
      setPhase('idle');
    }
  };

  if (phase === 'sent') {
    return (
      <div className="flex flex-col items-start gap-2">
        <p className="text-14 font-semibold text-text">{m.feedback_sent_title()}</p>
        <p className="text-13 text-text-2">
          {m.feedback_sent_body()}
        </p>
        <Button variant="line" className="mt-2" onClick={onClose}>
          {m.feedback_close()}
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <textarea
        aria-label={m.feedback_message_aria()}
        autoFocus
        rows={4}
        maxLength={2_000}
        className={`${fieldClass} resize-y`}
        placeholder={placeholder}
        value={message}
        disabled={phase === 'sending'}
        onChange={(event) => setMessage(event.target.value)}
      />
      {signedIn ? null : (
        <label className="block">
          <span className="mb-1.5 block text-12 text-text-2">{m.feedback_email_label()}</span>
          <input
            type="email"
            aria-label={m.feedback_email_label()}
            maxLength={320}
            className={fieldClass}
            placeholder={m.feedback_email_placeholder()}
            value={contact}
            disabled={phase === 'sending'}
            onChange={(event) => setContact(event.target.value)}
          />
        </label>
      )}
      {failure === '' ? null : (
        <p role="alert" className="text-13 text-c-pink">
          {failure}
        </p>
      )}
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={message.trim() === '' || phase === 'sending'}>
          {phase === 'sending' ? m.feedback_sending() : m.feedback_send()}
        </Button>
        <Button type="button" variant="ghost" onClick={onClose}>
          {m.feedback_cancel()}
        </Button>
      </div>
    </form>
  );
};
